import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  FileText, Send, MailOpen, Reply, Search, MapPin, Briefcase, BarChart3,
  CalendarDays, CheckCircle2, Clock, XCircle, Ban, MessageSquare, Paperclip,
  ChevronLeft, ChevronRight, Loader2, Layers, X, Plus, Activity, Mail,
} from "lucide-react";

const DELIVERY_META = {
  delivered: { label: "Delivered", cls: "bg-emerald-50 text-emerald-600 border-emerald-200", Icon: CheckCircle2 },
  sent: { label: "Sent", cls: "bg-sky-50 text-sky-600 border-sky-200", Icon: Send },
  pending: { label: "Pending", cls: "bg-amber-50 text-amber-600 border-amber-200", Icon: Clock },
  bounced: { label: "Bounced", cls: "bg-rose-50 text-rose-600 border-rose-200", Icon: XCircle },
  failed: { label: "Failed", cls: "bg-rose-50 text-rose-600 border-rose-200", Icon: Ban },
};
const OPEN_META = {
  opened: { label: "Opened", cls: "bg-violet-50 text-violet-600 border-violet-200", Icon: MailOpen },
  not_opened: { label: "Not Opened", cls: "bg-slate-100 text-slate-500 border-slate-200", Icon: Mail },
};
const REPLY_META = {
  replied: { label: "Replied", cls: "bg-emerald-50 text-emerald-600 border-emerald-200", Icon: MessageSquare },
  no_reply: { label: "No Reply", cls: "bg-slate-100 text-slate-500 border-slate-200", Icon: Clock },
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${fmtDate(iso)}, ${d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}`;
}

function Chip({ meta, value }) {
  const m = meta[value] || meta[Object.keys(meta)[0]];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold ${m.cls}`}>
      <m.Icon className="w-3.5 h-3.5" /> {m.label}
    </span>
  );
}

function Logo({ app, size = "w-9 h-9" }) {
  const [failed, setFailed] = useState(false);
  const src = app.domain ? `https://www.google.com/s2/favicons?domain=${app.domain}&sz=64` : null;
  if (!src || failed) {
    return (
      <div className={`${size} rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 font-bold shrink-0`}>
        {(app.company || "?")[0].toUpperCase()}
      </div>
    );
  }
  return <img src={src} alt={app.company} onError={() => setFailed(true)}
    className={`${size} rounded-lg object-contain bg-white border border-slate-200 p-1 shrink-0`} />;
}

const TIMELINE_STEPS = [
  { key: "applied", label: "Applied", note: "Application submitted" },
  { key: "delivered", label: "Delivered", note: "Delivered to recipient" },
  { key: "opened", label: "Opened", note: "Email opened by recipient" },
  { key: "replied", label: "Reply Received", note: "Recruiter replied" },
];

function Timeline({ timeline }) {
  const events = timeline || [];
  const find = (keys) => events.find((e) => keys.includes(e.status));
  const errorEvent = find(["bounced", "failed"]);
  return (
    <div data-testid="app-timeline" className="mt-1">
      {TIMELINE_STEPS.map((step, i) => {
        const ev = find([step.key, ...(step.key === "delivered" ? ["sent"] : [])]);
        const isLast = i === TIMELINE_STEPS.length - 1;
        return (
          <div key={step.key} className="flex gap-3">
            <div className="flex flex-col items-center">
              {ev ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                : <div className="w-5 h-5 rounded-full border-2 border-slate-200 bg-white shrink-0" />}
              {!isLast && <div className="w-0.5 flex-1 min-h-[22px] bg-slate-200" />}
            </div>
            <div className="pb-4 flex-1">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className={`text-sm font-semibold ${ev ? "text-slate-900" : "text-slate-400"}`}>{step.label}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{ev ? ev.note || "" : step.key === "replied" ? "Waiting for a reply…" : "Pending"}</p>
                </div>
                {ev && <span className="text-xs text-slate-400 whitespace-nowrap">{fmtDateTime(ev.at)}</span>}
              </div>
            </div>
          </div>
        );
      })}
      {errorEvent && (
        <div className="flex gap-3">
          <XCircle className="w-5 h-5 text-rose-500 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-rose-600 capitalize">{errorEvent.status}</p>
            <p className="text-xs text-slate-400 mt-0.5">{errorEvent.note} · {fmtDateTime(errorEvent.at)}</p>
          </div>
        </div>
      )}
      {events.filter((e) => e.status === "follow_up").map((e, i) => (
        <div key={`fu-${i}`} className="flex gap-3">
          <Send className="w-5 h-5 text-amber-500 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-700">{e.note?.startsWith("Reply") ? "Reply sent" : "Follow-up sent"}</p>
            <p className="text-xs text-slate-400 mt-0.5">{e.note} · {fmtDateTime(e.at)}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function MessageBubble({ m, candidateInitial }) {
  const outbound = m.direction === "outbound";
  return (
    <div data-testid={`conv-message-${m.direction}`} className="flex gap-3">
      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
        outbound ? "bg-amber-500 text-white" : "bg-violet-100 text-violet-700"
      }`}>
        {outbound ? candidateInitial : "R"}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-900">{m.from_name}</span>
          {outbound && <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">You</span>}
          {!outbound && <span className="text-[10px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5">Recruiter</span>}
          <span className="text-[11px] text-slate-400 ml-auto whitespace-nowrap">{fmtDateTime(m.at)}</span>
        </div>
        <div className={`mt-1.5 rounded-xl px-4 py-3 border ${outbound ? "bg-slate-50 border-slate-100" : "bg-violet-50/40 border-violet-100"}`}>
          {m.subject && <p className="text-sm font-semibold text-slate-800 mb-1">{m.subject}</p>}
          <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{m.body || "(No message content)"}</p>
          {m.attachments?.length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-2">
              {m.attachments.map((a, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-600">
                  <Paperclip className="w-3.5 h-3.5 text-slate-400" /> {a.filename}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Applications() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState({ total: 0, delivered: 0, opened: 0, replies: 0 });
  const [data, setData] = useState({ items: [], total: 0, locations: [] });
  const [filters, setFilters] = useState({ search: "", delivery: "", period: "all", location: "", company: "" });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [tab, setTab] = useState("conversation");
  const pageSize = 8;
  const pollRef = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [{ data: board }, { data: sum }] = await Promise.all([
        api.get("/applications/board", { params: { ...filters, page, page_size: pageSize } }),
        api.get("/applications/summary"),
      ]);
      setData(board);
      setSummary(sum);
      if (board.items.length && !board.items.find((a) => a.id === selectedId)) {
        setSelectedId(board.items[0].id);
      }
      if (!board.items.length) setSelectedId(null);
    } catch {
      if (!silent) toast.error("Could not load applications.");
    } finally {
      setLoading(false);
    }
  }, [filters, page, selectedId]);

  useEffect(() => { load(); }, [filters, page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    pollRef.current = setInterval(() => load(true), 30000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  useEffect(() => {
    if (!selectedId) { setThread(null); return; }
    setThreadLoading(true);
    setTab("conversation");
    api.get(`/communications/${selectedId}`).then(({ data }) => {
      setThread(data);
      if (data.unread > 0) {
        api.post(`/communications/${selectedId}/read`).then(() => {
          window.dispatchEvent(new Event("replies-updated"));
        }).catch(() => {});
      }
    }).catch(() => setThread(null)).finally(() => setThreadLoading(false));
  }, [selectedId]);

  const selected = data.items.find((a) => a.id === selectedId) || null;
  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));
  const showingTo = Math.min(page * pageSize, data.total);

  const CARDS = [
    { key: "total", label: "Total Applied", value: summary.total, Icon: FileText, cls: "bg-sky-50 text-sky-500" },
    { key: "delivered", label: "Delivered", value: summary.delivered, Icon: Send, cls: "bg-emerald-50 text-emerald-500" },
    { key: "opened", label: "Opened", value: summary.opened, Icon: MailOpen, cls: "bg-violet-50 text-violet-500" },
    { key: "replies", label: "Replies", value: summary.replies, Icon: Reply, cls: "bg-amber-50 text-amber-500" },
  ];
  const selectCls = "h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-600 outline-none focus:border-amber-500";
  const candidateInitial = (thread?.candidate_name || "Y")[0].toUpperCase();

  const goReply = () => navigate(`/app/communications?app=${selectedId}&reply=1`);
  const goThread = () => navigate(`/app/communications?app=${selectedId}`);

  return (
    <div data-testid="applications-page" className="space-y-5">
      {/* Header banner */}
      <div className="rounded-2xl bg-gradient-to-r from-amber-50 via-white to-amber-100/60 border border-amber-100 px-6 py-7 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-display">Applications</h1>
          <p className="text-slate-500 mt-1.5">Track every application sent through your network outreach.</p>
        </div>
        <Button data-testid="new-application-btn" onClick={() => navigate("/app/discover")}
          className="bg-amber-500 text-black hover:bg-amber-400 font-semibold shrink-0 hidden sm:inline-flex">
          <Plus className="w-4 h-4 mr-1.5" /> New Application
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {CARDS.map((c) => (
          <div key={c.key} data-testid={`summary-${c.key}`}
            className="rounded-2xl border border-slate-200 bg-white px-5 py-4 flex items-center gap-4">
            <div className={`w-11 h-11 rounded-xl ${c.cls} flex items-center justify-center`}>
              <c.Icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500">{c.label}</p>
              <p className="text-2xl font-bold text-slate-900">{c.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_460px] gap-5 items-start">
        {/* List card */}
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-slate-100">
            <h2 className="font-bold text-slate-900 mr-auto">Applications ({data.total})</h2>
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="applications-search-input" value={filters.search}
                onChange={(e) => { setFilters({ ...filters, search: e.target.value }); setPage(1); }}
                placeholder="Search company, role…"
                className="h-10 w-48 pl-9 pr-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-amber-500" />
            </div>
            <select data-testid="applications-company-filter" value={filters.company}
              onChange={(e) => { setFilters({ ...filters, company: e.target.value }); setPage(1); }} className={selectCls}>
              <option value="">All companies</option>
              {(data.companies || []).map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <select data-testid="applications-status-filter" value={filters.delivery}
              onChange={(e) => { setFilters({ ...filters, delivery: e.target.value }); setPage(1); }} className={selectCls}>
              <option value="">All statuses</option>
              <option value="delivered">Delivered</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
            </select>
            <select data-testid="applications-time-filter" value={filters.period}
              onChange={(e) => { setFilters({ ...filters, period: e.target.value }); setPage(1); }} className={selectCls}>
              <option value="all">All time</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="py-16 text-center"><Loader2 className="w-7 h-7 mx-auto animate-spin text-amber-500" /></div>
            ) : data.items.length === 0 ? (
              <div className="py-16 text-center text-slate-500">
                <Layers className="w-9 h-9 mx-auto mb-2 text-slate-300" />
                <p className="font-medium text-slate-700">No applications yet</p>
                <p className="text-sm mt-1">Apply to a job from Discover &amp; Apply to see it here.</p>
              </div>
            ) : (
              <table className="w-full min-w-[760px] text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold text-slate-400 border-b border-slate-100">
                    <th className="w-10 px-4 py-3"></th>
                    <th className="px-2 py-3">Company</th>
                    <th className="px-3 py-3">Role</th>
                    <th className="px-3 py-3 whitespace-nowrap">Applied On</th>
                    <th className="px-3 py-3">Delivery</th>
                    <th className="px-3 py-3">Opened</th>
                    <th className="px-3 py-3 pr-5">Reply</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {data.items.map((a) => {
                    const active = selectedId === a.id;
                    return (
                      <tr key={a.id} data-testid={`application-row-${a.id}`} onClick={() => setSelectedId(a.id)}
                        className={`cursor-pointer transition-colors ${active ? "bg-amber-50/70" : "hover:bg-slate-50"}`}>
                        <td className={`px-4 py-3 ${active ? "border-l-4 border-l-amber-500" : "border-l-4 border-l-transparent"}`}>
                          <span data-testid={`application-check-${a.id}`}
                            className={`inline-flex items-center justify-center w-5 h-5 rounded-md border-2 ${active ? "bg-amber-500 border-amber-500" : "border-slate-300 bg-white"}`}>
                            {active && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                          </span>
                        </td>
                        <td className="px-2 py-3">
                          <div className="flex items-center gap-2.5">
                            <Logo app={a} />
                            <p className="font-semibold text-slate-900 leading-snug max-w-[220px]">{a.company}</p>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{a.job_title || "—"}</td>
                        <td className="px-3 py-3 text-slate-500 whitespace-nowrap">{fmtDate(a.applied_at)}</td>
                        <td className="px-3 py-3"><Chip meta={DELIVERY_META} value={a.delivery_status} /></td>
                        <td className="px-3 py-3"><Chip meta={OPEN_META} value={a.open_status} /></td>
                        <td className="px-3 py-3 pr-5">
                          <div className="flex items-center gap-2">
                            <Chip meta={REPLY_META} value={a.reply_status} />
                            {a.unread_replies > 0 && (
                              <span data-testid={`unread-reply-dot-${a.id}`}
                                className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-black text-[10px] font-bold">
                                {a.unread_replies}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex items-center justify-between px-5 py-3.5 border-t border-slate-100">
            <p className="text-sm text-slate-500">Showing {data.total === 0 ? 0 : showingTo} of {data.total} applications</p>
            <div className="flex items-center gap-1.5">
              <button data-testid="apps-prev" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-sm font-semibold text-slate-700 px-2">{page}</span>
              <button data-testid="apps-next" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </div>

        {/* Preview panel */}
        <div className="rounded-2xl border border-slate-200 bg-white lg:sticky lg:top-24 flex flex-col max-h-[calc(100vh-7rem)]" data-testid="app-detail-panel">
          {!selected ? (
            <div className="py-20 text-center text-slate-500">
              <FileText className="w-9 h-9 mx-auto mb-2 text-slate-300" />
              <p className="font-medium text-slate-700">Select an application</p>
            </div>
          ) : (
            <>
              <div className="px-6 pt-5 pb-4 border-b border-slate-100">
                <div className="flex items-start gap-3">
                  <Logo app={selected} size="w-11 h-11" />
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-slate-900 leading-snug truncate" data-testid="detail-company">{selected.company}</p>
                    <p className="text-sm text-slate-600 truncate" data-testid="detail-role">{selected.job_title || "Application"}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-slate-500">
                      {selected.job_location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {selected.job_location}</span>}
                      {selected.job_type && <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> {selected.job_type}</span>}
                      <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Applied {fmtDate(selected.applied_at)}</span>
                    </div>
                  </div>
                  <Chip meta={REPLY_META} value={selected.reply_status} />
                </div>

                <div className="flex gap-1 mt-4 border-b border-slate-100 -mb-4">
                  {[["conversation", "Conversation"], ["details", "Application Details"], ["activity", "Activity"]].map(([k, label]) => (
                    <button key={k} data-testid={`app-tab-${k}`} onClick={() => setTab(k)}
                      className={`px-3 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                        tab === k ? "border-amber-500 text-amber-600" : "border-transparent text-slate-500 hover:text-slate-800"
                      }`}>{label}</button>
                  ))}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-5">
                {threadLoading ? (
                  <div className="py-10 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-amber-500" /></div>
                ) : tab === "conversation" ? (
                  <div className="space-y-5" data-testid="conversation-tab">
                    {(thread?.messages || []).length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-8">No messages in this conversation yet.</p>
                    ) : (
                      thread.messages.map((m) => <MessageBubble key={m.message_id} m={m} candidateInitial={candidateInitial} />)
                    )}
                  </div>
                ) : tab === "details" ? (
                  <div className="space-y-3 text-sm" data-testid="details-tab">
                    <Row label="Applied On" value={fmtDateTime(selected.applied_at)} />
                    <Row label="Email Template" value={selected.template_name || "—"} />
                    <div className="flex items-center justify-between gap-3"><span className="text-slate-500">Delivery</span><Chip meta={DELIVERY_META} value={selected.delivery_status} /></div>
                    <div className="flex items-center justify-between gap-3"><span className="text-slate-500">Open</span><Chip meta={OPEN_META} value={selected.open_status} /></div>
                    <div className="flex items-center justify-between gap-3"><span className="text-slate-500">Reply</span><Chip meta={REPLY_META} value={selected.reply_status} /></div>
                    {selected.job_exp && <Row label="Experience" value={selected.job_exp} />}
                    {selected.follow_up_count > 0 && <Row label="Replies / follow-ups sent" value={`×${selected.follow_up_count}`} />}
                  </div>
                ) : (
                  <div data-testid="activity-tab">
                    <Timeline timeline={thread?.timeline || selected.timeline} />
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-slate-100 flex items-center gap-3">
                <Button data-testid="open-thread-btn" variant="ghost" onClick={goThread}
                  className="text-slate-600 hover:text-slate-900">Open in Communications</Button>
                <Button data-testid="reply-button" onClick={goReply}
                  className="ml-auto bg-amber-500 text-black hover:bg-amber-400 font-semibold px-6">
                  <Reply className="w-4 h-4 mr-2" /> Reply
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900 text-right">{value}</span>
    </div>
  );
}
